// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import * as API from '../../api';
import { ValidateReleaseVersionRequest } from '../../actions/app';
import { CreateEditReleaseType } from '../../actions/release';
import { selectAuthCredentials } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { NewVersionValidationResult, ReleaseTag } from '../../types';

export const VERSION_REGEX = /\d+(?:\.\d+)?(?:\.\d+)?/;

export interface UseVersionValidationProps {
    orgId: string;
    appId: string;
    releaseId?: string;
    releaseType: CreateEditReleaseType;
}

export interface VersionValidationResult {
    isStaticBranch: boolean;
    branchName: string;
    hotfixBase: string;
    hotfixPreviousVersion: string;
    latestCompletedReleaseVersion: string;
    previousTags: ReleaseTag[];
    versionStringForPossibleBump: string;
    releasePilotFromRotation: string | null;
    isReleasePilotFromRotationScheduleIntegration: boolean;
    validationError: string | null;
}

export interface UseVersionValidationReturn {
    validateVersion: (version: string, sourceVersion?: string) => Promise<VersionValidationResult>;
    validateVersionFormat: (version: string) => boolean;
    resultIsStaticBranch: boolean;
    resultBranchName: string;
    hotfixBase: string;
    latestCompletedReleaseVersion: string;
    previousTags: ReleaseTag[];
    versionStringForPossibleBump: string;
    releasePilotFromRotation: string | null;
    isReleasePilotFromRotationScheduleIntegration: boolean;
    resetValidationState: () => void;
}

export const useVersionValidation = ({
    orgId,
    appId,
    releaseId,
    releaseType,
}: UseVersionValidationProps): UseVersionValidationReturn => {
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));

    const [resultIsStaticBranch, setResultIsStaticBranch] = React.useState(false);
    const [resultBranchName, setResultBranchName] = React.useState('');
    const [hotfixBase, setHotfixBase] = React.useState('');
    const [latestCompletedReleaseVersion, setLatestCompletedReleaseVersion] = React.useState('');
    const [previousTags, setPreviousTags] = React.useState<ReleaseTag[]>([]);
    const [versionStringForPossibleBump, setVersionStringForPossibleBump] = React.useState('');
    const [releasePilotFromRotation, setReleasePilotFromRotation] = React.useState<string | null>(null);
    const [isReleasePilotFromRotationScheduleIntegration, setIsReleasePilotFromRotationScheduleIntegration] =
        React.useState(false);

    const validateVersionFormat = React.useCallback((version: string): boolean => {
        return VERSION_REGEX.test(version);
    }, []);

    const resetValidationState = React.useCallback(() => {
        setResultIsStaticBranch(false);
        setResultBranchName('');
        setHotfixBase('');
        setLatestCompletedReleaseVersion('');
        setPreviousTags([]);
        setVersionStringForPossibleBump('');
        setReleasePilotFromRotation(null);
        setIsReleasePilotFromRotationScheduleIntegration(false);
    }, []);

    const validateVersion = React.useCallback(
        async (version: string, sourceVersion?: string): Promise<VersionValidationResult> => {
            const request: ValidateReleaseVersionRequest = {
                orgId,
                appId,
                version,
                releaseId: releaseId ?? null,
                releaseType,
                sourceVersion,
            };

            const result: NewVersionValidationResult = await API.validateReleaseVersion(request, authCredentials);

            setResultIsStaticBranch(result.isStaticBranch);

            if (result.validationError) {
                throw new Error(result.validationError);
            }

            if (result.branchName) {
                setResultBranchName(result.branchName);
            }
            if (result.hotfixBase) {
                setHotfixBase(result.hotfixBase);
            }
            if (result.latestCompletedReleaseVersion) {
                setLatestCompletedReleaseVersion(result.latestCompletedReleaseVersion);
            }
            if (result.previousTags) {
                setPreviousTags(result.previousTags);
            }
            if (result.versionStringForPossibleBump) {
                setVersionStringForPossibleBump(result.versionStringForPossibleBump);
            }
            if (result.releasePilotFromRotation) {
                setReleasePilotFromRotation(result.releasePilotFromRotation);
            }
            setIsReleasePilotFromRotationScheduleIntegration(result.isReleasePilotFromRotationScheduleIntegration);

            return {
                isStaticBranch: result.isStaticBranch,
                branchName: result.branchName,
                hotfixBase: result.hotfixBase,
                hotfixPreviousVersion: result.hotfixPreviousVersion,
                latestCompletedReleaseVersion: result.latestCompletedReleaseVersion,
                previousTags: result.previousTags,
                versionStringForPossibleBump: result.versionStringForPossibleBump,
                releasePilotFromRotation: result.releasePilotFromRotation,
                isReleasePilotFromRotationScheduleIntegration: result.isReleasePilotFromRotationScheduleIntegration,
                validationError: result.validationError,
            };
        },
        [orgId, appId, releaseId, releaseType, authCredentials],
    );

    return {
        validateVersion,
        validateVersionFormat,
        resultIsStaticBranch,
        resultBranchName,
        hotfixBase,
        latestCompletedReleaseVersion,
        previousTags,
        versionStringForPossibleBump,
        releasePilotFromRotation,
        isReleasePilotFromRotationScheduleIntegration,
        resetValidationState,
    };
};
